// The menu: one overlay inside the game page with three pages: home (the
// lobby: nickname, quick play, rooms), Controls & settings, Create a room.
// Shown on a plain visit, or over the running match (the connection stays
// up) with Esc, the touch ☰ button or the scoreboard's menu entry. Esc
// steps out one level: sub-page -> home -> game. Plain DOM on /api/rooms,
// /api/levels and the kit's auth routes. docs/screens/menu.md.
import type Phaser from 'phaser';
import { ApiError, api, clear, el, installAuthBox, installBindingsTable, platform, type AccountInfo } from '@platoon/client-kit';
import { MATCH_PLAYING, MATCH_WARMUP } from '@mayday/shared';
import { ACTIONS, RESERVED_KEY_CODES, SETTINGS_CHANGED, bindingStore, setMoveMode, setNickname, setShowDebug, settings, type MoveMode } from './settings';
import './lobby.css';

// game.events: the game scene asks for the menu (plain visit, nothing to
// connect to), the HUD asks for it (☰, scoreboard), the menu asks the game
// scene to play somewhere, and asks the HUD to drop a pinned scoreboard.
export const LOBBY_OPEN = 'lobby:open';
export const PLAY_REQUEST = 'lobby:play';
export const UNPIN_BOARD = 'lobby:unpin';

type Page = 'home' | 'controls' | 'create';

export interface PlayTarget {
  room?: string;
  mode?: 'dm' | 'team';
}

interface RoomRow {
  id: string;
  name: string;
  mode: 'dm' | 'team';
  teams: number;
  teamSize: number;
  level: string;
  players: number;
  bots: number;
  maxPlayers: number;
  matchState: number;
  secondsLeft: number;
  isMain: boolean;
  mine: boolean;
}

interface RoomsResponse {
  rooms: RoomRow[];
  motd: string;
  canCreate: boolean;
}

interface LevelRow {
  name: string;
  minPlayers: number;
  maxPlayers: number;
}

export interface Lobby {
  open(): void;
  close(): void;
}

const MY_ROOMS_KEY = 'myRooms'; // guests: ids of rooms created in this browser
const REFRESH_MS = 3000;

function modeLabel(r: RoomRow): string {
  if (r.mode !== 'team') return 'Deathmatch';
  return r.teams === 2 ? `Team ${r.teamSize}v${r.teamSize}` : `Team ${r.teams}×${r.teamSize}`;
}

function matchLabel(r: RoomRow): string {
  const m = Math.floor(r.secondsLeft / 60);
  const s = r.secondsLeft % 60;
  if (r.matchState === MATCH_WARMUP) return `warm-up · starts in ${r.secondsLeft}s`;
  return r.matchState === MATCH_PLAYING ? `${m}:${s < 10 ? '0' : ''}${s} left` : `next match in ${r.secondsLeft}s`;
}

function loadMyRooms(): string[] {
  try {
    const v: unknown = JSON.parse(platform.loadSetting(MY_ROOMS_KEY) ?? '[]');
    return Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : [];
  } catch {
    return [];
  }
}

export function installLobby(cfg: { game: Phaser.Game; root: HTMLElement; isTouch: boolean; isBoardPinned: () => boolean }): Lobby {
  const { game, root } = cfg;
  const q = (sel: string): HTMLElement => root.querySelector<HTMLElement>(sel)!;
  const backBtn = root.querySelector<HTMLButtonElement>('#lobby-back')!;
  const pages: Record<Page, HTMLElement> = { home: q('#page-home'), controls: q('#page-controls'), create: q('#page-create') };
  let page: Page = 'home';
  const nick = root.querySelector<HTMLInputElement>('#lobby-nick')!;
  const nickLabel = q('#lobby-nick-label');
  const moveModeSelect = root.querySelector<HTMLSelectElement>('#move-mode')!;
  const toCreate = root.querySelector<HTMLButtonElement>('#to-create')!;
  const motd = q('#lobby-motd');
  const quickDm = root.querySelector<HTMLButtonElement>('#quick-dm')!;
  const quickTeam = root.querySelector<HTMLButtonElement>('#quick-team')!;
  const mineSection = q('#lobby-mine');
  const mineBody = root.querySelector<HTMLTableSectionElement>('#lobby-mine tbody')!;
  const roomsBody = root.querySelector<HTMLTableSectionElement>('#lobby-rooms tbody')!;
  const form = root.querySelector<HTMLFormElement>('#lobby-create-form')!;
  const createNote = q('#lobby-create-note');
  const adminLink = root.querySelector<HTMLAnchorElement>('#lobby-admin')!;
  const levelSelect = form.elements.namedItem('level') as HTMLSelectElement;
  const modeSelect = form.elements.namedItem('mode') as HTMLSelectElement;

  let account: AccountInfo | null = null;
  let canCreate = false;
  let myRooms = loadMyRooms();
  let timer: ReturnType<typeof setInterval> | null = null;
  let levelsLoaded = false;

  const inGame = (): boolean => game.registry.get('myId') !== -1 && game.registry.get('myId') !== undefined;

  function play(target: PlayTarget): void {
    close();
    game.events.emit(PLAY_REQUEST, target);
  }

  function roomRow(r: RoomRow): HTMLTableRowElement {
    const full = r.players >= r.maxPlayers;
    const here = game.registry.get('room') === r.id && inGame();
    const join = el('button', { type: 'button', class: here ? 'secondary' : '' }, here ? 'Back' : full ? 'Full' : 'Join');
    join.disabled = full && !here;
    join.addEventListener('click', () => (here ? close() : play({ room: r.id })));
    const invite = el('button', { type: 'button', class: 'secondary', title: 'Copy invite link' }, '🔗');
    invite.addEventListener('click', () => {
      void platform.copyText(platform.inviteLink(r.id)).then((ok) => {
        invite.textContent = ok ? 'Copied' : platform.inviteLink(r.id);
        setTimeout(() => (invite.textContent = '🔗'), 1500);
      });
    });
    return el(
      'tr',
      {},
      el('td', {}, r.name, r.mine ? el('span', { class: 'dim' }, ' · yours') : ''),
      el('td', {}, modeLabel(r)),
      el('td', { class: 'dim' }, r.level),
      el('td', {}, `${r.players} / ${r.maxPlayers}`, r.bots > 0 ? el('span', { class: 'dim' }, ` +${r.bots} bot${r.bots === 1 ? '' : 's'}`) : ''),
      el('td', { class: 'dim' }, matchLabel(r)),
      el('td', { class: 'actions' }, join, invite),
    );
  }

  async function refresh(): Promise<void> {
    let res: RoomsResponse;
    try {
      res = await api<RoomsResponse>('/api/rooms');
    } catch {
      clear(roomsBody);
      roomsBody.append(el('tr', {}, el('td', { colspan: '6', class: 'dim' }, 'Server unreachable')));
      return;
    }
    motd.textContent = res.motd;
    canCreate = res.canCreate || account?.role === 'admin';
    toCreate.hidden = !canCreate;
    backBtn.hidden = !inGame();
    // Quick play cards show who is playing right now.
    let dmPlayers = 0;
    let teamPlayers = 0;
    for (const r of res.rooms) {
      if (r.mode === 'team') teamPlayers += r.players;
      else dmPlayers += r.players;
    }
    q('#quick-dm .sub').textContent = `${dmPlayers} playing now · up to 20 in a room`;
    q('#quick-team .sub').textContent = `${teamPlayers} playing now · 3 vs 3`;
    // Rooms: yours first (server flag for accounts, browser memory for guests).
    myRooms = myRooms.filter((id) => res.rooms.some((r) => r.id === id));
    platform.saveSetting(MY_ROOMS_KEY, JSON.stringify(myRooms));
    const mine = res.rooms.filter((r) => r.mine || myRooms.includes(r.id));
    const others = res.rooms.filter((r) => !mine.includes(r));
    clear(mineBody);
    mineSection.hidden = mine.length === 0;
    for (const r of mine) mineBody.append(roomRow(r));
    clear(roomsBody);
    if (others.length === 0) roomsBody.append(el('tr', {}, el('td', { colspan: '6', class: 'dim' }, 'No other rooms')));
    for (const r of others) roomsBody.append(roomRow(r));
  }

  async function loadLevels(): Promise<void> {
    if (levelsLoaded) return;
    const { levels } = await api<{ levels: LevelRow[] }>('/api/levels');
    levelsLoaded = true;
    clear(levelSelect);
    levelSelect.append(el('option', { value: '' }, 'default for the mode'));
    for (const lv of levels) levelSelect.append(el('option', { value: lv.name }, `${lv.name} (${lv.minPlayers}–${lv.maxPlayers} players)`));
  }

  function showModeFields(): void {
    for (const label of form.querySelectorAll<HTMLElement>('label[data-mode]')) label.hidden = label.dataset['mode'] !== modeSelect.value;
  }
  modeSelect.addEventListener('change', showModeFields);

  form.addEventListener('submit', (e) => {
    e.preventDefault();
    createNote.textContent = '';
    const f = new FormData(form);
    const num = (k: string): number => Number(f.get(k));
    const str = (k: string): string => {
      const v = f.get(k);
      return typeof v === 'string' ? v : '';
    };
    const body = {
      name: str('name'),
      mode: modeSelect.value,
      level: str('level') || undefined,
      maxPlayers: num('maxPlayers'),
      teams: num('teams'),
      teamSize: num('teamSize'),
      friendlyFire: f.get('friendlyFire') === 'true',
      killLimit: num('killLimit'),
      timeLimitS: num('timeLimitMin') * 60,
    };
    void api<{ id: string }>('/api/rooms', 'POST', body)
      .then((r) => {
        myRooms.push(r.id);
        platform.saveSetting(MY_ROOMS_KEY, JSON.stringify(myRooms));
        show('home');
        play({ room: r.id });
      })
      .catch((err: unknown) => {
        createNote.textContent = err instanceof ApiError ? err.message : 'failed';
      });
  });

  quickDm.addEventListener('click', () => play({ mode: 'dm' }));
  quickTeam.addEventListener('click', () => play({ mode: 'team' }));
  backBtn.addEventListener('click', close);

  // ---- pages ----------------------------------------------------------------
  function show(next: Page): void {
    if (page === 'controls' && next !== 'controls') bindings.cancel();
    page = next;
    for (const k of Object.keys(pages) as Page[]) pages[k].hidden = k !== next;
    if (next === 'controls') bindings.render();
    if (next === 'create') void loadLevels().catch(() => undefined);
    root.scrollTop = 0;
  }
  q('#to-controls').addEventListener('click', () => show('controls'));
  toCreate.addEventListener('click', () => show('create'));
  for (const b of root.querySelectorAll<HTMLButtonElement>('.page-back')) b.addEventListener('click', () => show('home'));

  // ---- settings on the pages (saved as they change, no Done button) ----------
  nick.value = settings.nickname;
  const saveNick = (): void => {
    if (nickLabel.hidden) return; // logged in: the account name is the nickname
    nick.value = setNickname(nick.value);
    game.events.emit(SETTINGS_CHANGED);
  };
  nick.addEventListener('change', saveNick);
  nick.addEventListener('keydown', (ev) => {
    if (ev.key === 'Enter') nick.blur();
  });
  const showDebug = root.querySelector<HTMLInputElement>('#show-debug')!;
  showDebug.checked = settings.showDebug;
  showDebug.addEventListener('change', () => {
    setShowDebug(showDebug.checked);
    game.events.emit(SETTINGS_CHANGED);
  });
  game.events.on(SETTINGS_CHANGED, () => {
    showDebug.checked = settings.showDebug; // F3 in game
  });
  moveModeSelect.value = settings.moveMode;
  moveModeSelect.addEventListener('change', () => {
    setMoveMode(moveModeSelect.value as MoveMode);
    game.events.emit(SETTINGS_CHANGED);
  });
  if (cfg.isTouch) {
    q('#move-mode-field').hidden = true; // twin sticks are always screen-relative
    q('#controls-desktop').hidden = true;
    q('#touch-help').hidden = false;
    q('#lobby-hint').textContent = 'Tap ☰ to open and close this menu; tap the score for the scoreboard.';
  }
  const bindings = installBindingsTable({
    game,
    table: root.querySelector<HTMLTableElement>('#bindings')!,
    note: q('#bindings-note'),
    resetButton: root.querySelector<HTMLButtonElement>('#reset-bindings')!,
    actions: ACTIONS,
    store: bindingStore,
    reserved: RESERVED_KEY_CODES,
    isActive: () => !root.hidden && page === 'controls',
  });

  // ---- Esc: one key, one meaning: step out one level ------------------------
  // Closed: a pinned scoreboard closes first, else the menu opens. Open: a
  // sub-page goes back to the lobby; the lobby returns to the game (on the
  // landing, with no game behind, Esc does nothing). Capture phase, so the
  // press never reaches the game.
  window.addEventListener(
    'keydown',
    (ev) => {
      if (ev.key !== 'Escape') return;
      if (document.activeElement === nick) {
        nick.blur();
        ev.preventDefault();
        return;
      }
      ev.stopImmediatePropagation();
      ev.preventDefault();
      if (root.hidden) {
        if (cfg.isBoardPinned()) game.events.emit(UNPIN_BOARD);
        else if (inGame()) open();
        return;
      }
      if (page !== 'home') show('home');
      else if (inGame()) close();
    },
    true,
  );

  function open(): void {
    if (!root.hidden) return;
    root.hidden = false;
    show('home');
    game.events.emit(UNPIN_BOARD);
    game.registry.set('uiOpen', true); // neutral input, no shots while the overlay is up
    game.input.keyboard!.preventDefault = false;
    backBtn.hidden = !inGame();
    void refresh();
    timer = setInterval(() => void refresh(), REFRESH_MS);
  }

  function close(): void {
    if (root.hidden) return;
    bindings.cancel();
    saveNick();
    root.hidden = true;
    game.registry.set('uiOpen', false);
    game.input.keyboard!.preventDefault = true;
    if (timer) clearInterval(timer);
    timer = null;
  }

  // Logged in: the account name is the nickname; the field hides.
  function applyAccount(a: AccountInfo | null): void {
    nickLabel.hidden = a !== null;
    if (a) nick.value = a.name;
  }

  void installAuthBox({
    root: q('#lobby-auth'),
    onChange(a) {
      account = a;
      applyAccount(a);
      adminLink.hidden = a?.role !== 'admin';
      if (!root.hidden) void refresh();
    },
  });
  game.events.on(LOBBY_OPEN, open);
  return { open, close };
}
